import {
  AVERAGE_FACE_MIN_BREAKPOINT,
  AVERAGE_FACE_MAX_BREAKPOINT,
  NARROW_FACE_BREAKPOINT,
  WIDE_FACE_BREAKPOINT,
  EXTENDED_AVERAGE_FACE_MAX_BREAKPOINT,
  EXTENDED_OOS_RANGE_SIZES,
  EXTENDED_AVERAGE_FACE_MIN_BREAKPOINT,
} from '@/scripts/constants/pdp'
import { Size, FaceShape, Product, Availability } from '@/types/pdp'

export class Sizes {
  static convertSizeToFaceShape(sizes: Size[], isJunior: boolean): Size[] {
    if (isJunior) {
      return sizes
    }

    const sortedFaceShapeSizes = sizes?.sort(
      (a, b) => parseInt(a.hingeDistance) - parseInt(b.hingeDistance)
    )

    sortedFaceShapeSizes?.map((size) => {
      const hingeDistance = parseInt(size.hingeDistance)
      size.faceShape = []
      size.faceShapeLabel = ''

      this.findMostSoldFaceShape(size, hingeDistance)

      return size
    })

    const faceShapeSizes = this.findFaceShape(sortedFaceShapeSizes)
    this.faceShapeConversionCheck(faceShapeSizes)

    return faceShapeSizes
  }

  static findMostSoldFaceShape(
    size: Size,
    hingeDistance: number
  ): { faceShape: string[]; faceShapeLabel: string } {
    if (
      size.isMostSoldSize &&
      hingeDistance >= NARROW_FACE_BREAKPOINT &&
      hingeDistance <= WIDE_FACE_BREAKPOINT
    ) {
      size.faceShape?.push(FaceShape.AVERAGE_FACE)
      return {
        faceShape: size.faceShape || [],
        faceShapeLabel: size.faceShapeLabel || '',
      }
    }
    if (size.isMostSoldSize && hingeDistance > WIDE_FACE_BREAKPOINT) {
      size.faceShape?.push(FaceShape.WIDE_FACE)
      return {
        faceShape: size.faceShape || [],
        faceShapeLabel: size.faceShapeLabel || '',
      }
    }
    if (size.isMostSoldSize && hingeDistance < NARROW_FACE_BREAKPOINT) {
      size.faceShape?.push(FaceShape.NARROW_FACE)
      return {
        faceShape: size.faceShape || [],
        faceShapeLabel: size.faceShapeLabel || '',
      }
    }

    return {
      faceShape: [],
      faceShapeLabel: '',
    }
  }

  static findFaceShape(sizes: Size[]): Size[] {
    if (sizes?.length >= 2) {
      this.faceShapeSizesAvailable(sizes)
      this.faceShapeConversionCheck(sizes) ? sizes : this.addMissingFaceShape(sizes)
    }
    if (sizes?.length === 1) {
      this.onlyOneSizeAvailable(sizes)
    }
    return sizes
  }

  static faceShapeSizesAvailable(sizes: Size[]): Size[] {
    const maxHingeDistance = Math.max(...sizes.map((size) => parseInt(size.hingeDistance)))
    const minHingeDistance = Math.min(...sizes.map((size) => parseInt(size.hingeDistance)))

    return sizes.map((size) => {
      const hingeDistance = parseInt(size.hingeDistance)

      if (
        hingeDistance === maxHingeDistance &&
        hingeDistance >= AVERAGE_FACE_MAX_BREAKPOINT &&
        !size.faceShape?.includes(FaceShape.WIDE_FACE)
      ) {
        size.faceShape?.push(FaceShape.WIDE_FACE)
      } else if (
        hingeDistance === minHingeDistance &&
        hingeDistance <= AVERAGE_FACE_MIN_BREAKPOINT &&
        !size.faceShape?.includes(FaceShape.NARROW_FACE)
      ) {
        size.faceShape?.push(FaceShape.NARROW_FACE)
      } else if (
        hingeDistance !== minHingeDistance &&
        hingeDistance !== maxHingeDistance &&
        hingeDistance > AVERAGE_FACE_MIN_BREAKPOINT &&
        hingeDistance < AVERAGE_FACE_MAX_BREAKPOINT &&
        !size.faceShape?.includes(FaceShape.AVERAGE_FACE)
      ) {
        size.faceShape?.push(FaceShape.AVERAGE_FACE)
      }
      return size
    })
  }

  static onlyOneSizeAvailable(sizes: Size[]): Size[] {
    if (parseInt(sizes[0].hingeDistance) >= WIDE_FACE_BREAKPOINT) {
      if (sizes[0]) {
        sizes[0].faceShape?.push(FaceShape.WIDE_FACE)
      }
    } else if (parseInt(sizes[0].hingeDistance) <= NARROW_FACE_BREAKPOINT) {
      if (sizes[0]) {
        sizes[0].faceShape?.push(FaceShape.NARROW_FACE)
      }
    } else {
      if (sizes[0]) {
        sizes[0].faceShape?.push(FaceShape.AVERAGE_FACE)
      }
    }

    return sizes
  }

  static faceShapeConversionCheck(sizes: Size[]): boolean {
    const faceShapeSet = new Set<string>()

    sizes?.forEach((size) => {
      size.faceShape?.forEach((faceShape) => {
        faceShapeSet.add(faceShape)
      })
    })

    const allFacesAreInSet =
      faceShapeSet.has(FaceShape.NARROW_FACE) &&
      faceShapeSet.has(FaceShape.AVERAGE_FACE) &&
      faceShapeSet.has(FaceShape.WIDE_FACE)

    const allSizesAreFilled = this.allSizesAreFilled(sizes)

    return allFacesAreInSet && allSizesAreFilled
  }

  static allSizesAreFilled(sizes: Size[]): boolean {
    return sizes?.every((size) => size.faceShape && size.faceShape.length >= 1)
  }

  static fillSizes(
    sizes: Size[],
    faceShapeSet: Set<string>,
    maxHingeDistance: number,
    minHingeDistance: number
  ): Size[] {
    sizes.forEach((size) => {
      if (!this.allSizesAreFilled(sizes)) {
        if (parseInt(size.hingeDistance) === minHingeDistance && size.faceShape?.length === 0) {
          size.faceShape?.push(FaceShape.NARROW_FACE)
          faceShapeSet.add(FaceShape.NARROW_FACE)
        } else if (
          parseInt(size.hingeDistance) === maxHingeDistance &&
          size.faceShape?.length === 0
        ) {
          size.faceShape?.push(FaceShape.WIDE_FACE)
          faceShapeSet.add(FaceShape.WIDE_FACE)
        }
      }
      this.normalizeAverageSize(sizes)
    })

    const doubleSizesArray = this.checkDoubleSize(sizes)
    return doubleSizesArray
  }

  static normalizeAverageSize(sizes: Size[]): Size[] {
    if (this.allSizesAreFilled(sizes) && sizes.length > 2) {
      sizes.forEach((size) => {
        const averageSizeArray = size.faceShape?.includes(FaceShape.AVERAGE_FACE)
        if (averageSizeArray && size.faceShape && size.faceShape?.length > 1) {
          size.faceShape = size.faceShape.filter(
            (faceShape) => faceShape === FaceShape.AVERAGE_FACE
          )
        }
      })
    }

    return sizes
  }

  static addMissingFaceShape(sizes: Size[]): Size[] {
    const faceShapeSet = new Set<string>()
    const maxHingeDistance = Math.max(...sizes.map((size) => parseInt(size.hingeDistance)))
    const minHingeDistance = Math.min(...sizes.map((size) => parseInt(size.hingeDistance)))

    sizes.forEach((size) => {
      size.faceShape?.forEach((faceShape) => {
        faceShapeSet.add(faceShape)
      })
    })

    sizes.forEach((size) => {
      if (!faceShapeSet.has(FaceShape.NARROW_FACE)) {
        if (
          parseInt(size.hingeDistance) <= WIDE_FACE_BREAKPOINT &&
          parseInt(size.hingeDistance) !== maxHingeDistance
        ) {
          size.faceShape?.push(FaceShape.NARROW_FACE)
          faceShapeSet.add(FaceShape.NARROW_FACE)
        }
      }
      if (
        parseInt(size.hingeDistance) > NARROW_FACE_BREAKPOINT &&
        parseInt(size.hingeDistance) < WIDE_FACE_BREAKPOINT
      ) {
        if (!faceShapeSet.has(FaceShape.AVERAGE_FACE)) {
          size.faceShape?.push(FaceShape.AVERAGE_FACE)
          faceShapeSet.add(FaceShape.AVERAGE_FACE)
        }
      }
      if (!faceShapeSet.has(FaceShape.WIDE_FACE)) {
        if (
          parseInt(size.hingeDistance) >= NARROW_FACE_BREAKPOINT &&
          parseInt(size.hingeDistance) !== minHingeDistance
        ) {
          size.faceShape?.push(FaceShape.WIDE_FACE)
          faceShapeSet.add(FaceShape.WIDE_FACE)
        }
      }
    })

    this.fillSizes(sizes, faceShapeSet, maxHingeDistance, minHingeDistance)
    return sizes
  }

  static checkDoubleSize(sizes: Size[]): Size[] {
    const maxHingeDistance = Math.max(...sizes.map((size) => parseInt(size.hingeDistance)))
    const minHingeDistance = Math.min(...sizes.map((size) => parseInt(size.hingeDistance)))

    if (!this.allSizesAreFilled(sizes)) {
      return sizes.map((size) => {
        const hingeDistance = parseInt(size.hingeDistance)
        if (
          hingeDistance === minHingeDistance &&
          size.faceShape?.includes(FaceShape.AVERAGE_FACE)
        ) {
          size.faceShape?.push(FaceShape.NARROW_FACE)
        }
        if (
          hingeDistance === maxHingeDistance &&
          size.faceShape?.includes(FaceShape.AVERAGE_FACE)
        ) {
          size.faceShape?.push(FaceShape.WIDE_FACE)
        }
        if (
          hingeDistance !== minHingeDistance &&
          hingeDistance !== maxHingeDistance &&
          size.faceShape?.length === 0
        ) {
          hingeDistance >= AVERAGE_FACE_MAX_BREAKPOINT
            ? size.faceShape?.push(FaceShape.WIDE_FACE)
            : size.faceShape?.push(FaceShape.NARROW_FACE)
        }
        return size
      })
    }
    if (this.allSizesAreFilled(sizes) && sizes.length == 2) {
      if (sizes[0].faceShape && sizes[0].faceShape[0]) {
        const allSizesBelowNarrow = sizes.every(
          (size) => parseInt(size.hingeDistance) < AVERAGE_FACE_MIN_BREAKPOINT
        )
        const allSizesAboveAverage = sizes.every(
          (size) => parseInt(size.hingeDistance) > AVERAGE_FACE_MAX_BREAKPOINT
        )
        if (allSizesBelowNarrow) {
          return sizes.map((size, index) => {
            if (index === 0) {
              size.faceShape = []
              size.faceShape?.push(FaceShape.NARROW_FACE)
            } else {
              size.faceShape = []
              size.faceShape?.push(
                FaceShape.NARROW_FACE,
                FaceShape.AVERAGE_FACE,
                FaceShape.WIDE_FACE
              )
            }
            return size
          })
        }
        if (allSizesAboveAverage) {
          return sizes.map((size, index) => {
            if (index === 0) {
              size.faceShape = []
              size.faceShape?.push(
                FaceShape.NARROW_FACE,
                FaceShape.AVERAGE_FACE,
                FaceShape.WIDE_FACE
              )
            } else {
              size.faceShape = []
              size.faceShape?.push(FaceShape.WIDE_FACE)
            }
            return size
          })
        }
      }
    }
    if (this.allSizesAreFilled(sizes) && sizes.length > 2) {
      return sizes.map((size) => {
        const hingeDistance = parseInt(size.hingeDistance)
        if (
          hingeDistance < AVERAGE_FACE_MIN_BREAKPOINT &&
          size.faceShape?.includes(FaceShape.AVERAGE_FACE)
        ) {
          size.faceShape?.push(FaceShape.NARROW_FACE)
        }
        if (
          hingeDistance > AVERAGE_FACE_MAX_BREAKPOINT &&
          size.faceShape?.includes(FaceShape.AVERAGE_FACE)
        ) {
          size.faceShape?.push(FaceShape.WIDE_FACE)
        }

        return size
      })
    }
    return sizes
  }

  static normalizeSelectedProduct(selectedProduct: Product): Size {
    //TODO: capire come gestire avaialability
    const selectedProductFaceShape = {
      catEntryId: selectedProduct.catEntryId ?? '',
      faceShape: selectedProduct.sizes?.find(
        (size: Size) => size.catEntryId === selectedProduct.catEntryId
      )?.faceShape,
      faceShapeLabel: selectedProduct.sizes?.find(
        (size: Size) => size.catEntryId === selectedProduct.catEntryId
      )?.faceShapeLabel,
      hingeDistance:
        selectedProduct.sizes?.find((size: Size) => size.catEntryId === selectedProduct.catEntryId)
          ?.hingeDistance ?? '',
      isMostSoldSize:
        selectedProduct.sizes?.find((size: Size) => size.catEntryId === selectedProduct.catEntryId)
          ?.isMostSoldSize ?? false,
      label:
        selectedProduct.sizes?.find((size: Size) => size.catEntryId === selectedProduct.catEntryId)
          ?.label ?? '',
      size:
        selectedProduct.sizes?.find((size: Size) => size.catEntryId === selectedProduct.catEntryId)
          ?.size ?? '',
    }

    return selectedProductFaceShape
  }

  static evaluateSizeNotificationPopUp(hingeDistance: string, selectedFace: string): boolean {
    const hingeDist = parseFloat(hingeDistance)

    switch (selectedFace) {
      case FaceShape.AVERAGE_FACE:
        return (
          (hingeDist < NARROW_FACE_BREAKPOINT &&
            hingeDist >= EXTENDED_AVERAGE_FACE_MIN_BREAKPOINT) ||
          hingeDist < EXTENDED_AVERAGE_FACE_MIN_BREAKPOINT ||
          (hingeDist > WIDE_FACE_BREAKPOINT && hingeDist <= EXTENDED_AVERAGE_FACE_MAX_BREAKPOINT) ||
          hingeDist > EXTENDED_AVERAGE_FACE_MAX_BREAKPOINT
        )

      case FaceShape.NARROW_FACE:
        return (
          (hingeDist > AVERAGE_FACE_MIN_BREAKPOINT && hingeDist <= EXTENDED_OOS_RANGE_SIZES) ||
          hingeDist > EXTENDED_OOS_RANGE_SIZES
        )

      case FaceShape.WIDE_FACE:
        return (
          hingeDist < EXTENDED_OOS_RANGE_SIZES ||
          (hingeDist > EXTENDED_OOS_RANGE_SIZES && hingeDist < AVERAGE_FACE_MAX_BREAKPOINT)
        )

      default:
        return false
    }
  }

  static keepSelectedSize(selectedFaceSize: string, product: Product): string | undefined {
    const matchingSize = product.sizes?.find((size) => size.size === selectedFaceSize)
    if (matchingSize) {
      return matchingSize.catEntryId
    } else {
      return undefined
    }
  }
}
